import { useCallback, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'src/app/store/hooks';
import {
  TagRead,
  useTripsRetrieveQuery,
  useTripTagsCreateMutation,
} from 'src/common/external/bambi-api/bambiApi';
import { TagModal } from 'src/common/tags/TagModal';
import formatServerError from 'src/common/util/serverErrorFormatter';

import { tripSlice } from '../trip.slice';
import { selectOptionsToTripTripTags } from './selectOptionsToTripTags';

import type { SelectOptionWithColor } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

export default function TripTagModal() {
  const dispatch = useAppDispatch();
  const { tripTagModal } = useAppSelector((state) => state.trip);
  const [error, setError] = useState<string | null>(null);
  const [saveTripTags] = useTripTagsCreateMutation();

  const {
    data: selectedTrip,
    isLoading: isTripLoading,
    isFetching: isTripFetching,
    error: tripError,
  } = useTripsRetrieveQuery(
    {
      id: tripTagModal.tripId,
    },
    {
      skip: !tripTagModal.tripId,
    }
  );

  const closeModal = useCallback(() => {
    setError(null);
    dispatch(tripSlice.actions.onCloseTripTags());
  }, [setError, dispatch]);

  const handleSubmit = useCallback(
    async (selectedTags: SelectOptionWithColor[], allTags: TagRead[]) => {
      try {
        const tripTripTags = selectOptionsToTripTripTags(
          selectedTags,
          allTags,
          selectedTrip
        );

        await saveTripTags({
          tripTagBulkCreate: { tags: tripTripTags },
        }).unwrap();
        closeModal();
      } catch (error) {
        const message = formatServerError(error);
        setError(message);
      }
    },
    [closeModal, saveTripTags, selectedTrip]
  );

  return (
    <TagModal
      header="Add Trip Tags"
      initialTagIds={selectedTrip?.tags?.map((tripTag) => tripTag.tag)}
      modalProps={{
        open: tripTagModal.isOpen,
        setOpen: closeModal,
      }}
      isExternalLoading={isTripLoading || isTripFetching}
      externalError={tripError ? 'Failed to load existing trip tags' : error}
      handleSaveTags={handleSubmit}
    />
  );
}
